export type PreparedDownload = {
  objectUrl: string
  filename: string
}

const sanitizeDownloadFilename = (value: string, fallback: string) => {
  const sanitized = value.replace(/[\x00-\x1f\x7f/\\?%*:|"<>]/g, "_").trim()

  return sanitized || fallback
}

export const filenameFromContentDisposition = (
  contentDisposition: string | null | undefined,
  fallback: string,
) => {
  if (!contentDisposition) return fallback

  const parts = contentDisposition.split(";").map((part) => part.trim())
  const encodedFilename = parts.find((part) => part.toLowerCase().startsWith("filename*="))

  if (encodedFilename) {
    const rawValue = encodedFilename.slice(encodedFilename.indexOf("=") + 1).replace(/^"|"$/g, "")
    const normalized = rawValue.toLowerCase().startsWith("utf-8''")
      ? rawValue.slice("utf-8''".length)
      : rawValue
    try {
      return sanitizeDownloadFilename(decodeURIComponent(normalized), fallback)
    } catch {
      return sanitizeDownloadFilename(normalized, fallback)
    }
  }

  const plainFilename = parts.find((part) => part.toLowerCase().startsWith("filename="))

  if (!plainFilename) return fallback

  const rawValue = plainFilename.slice(plainFilename.indexOf("=") + 1).replace(/^"|"$/g, "")

  return sanitizeDownloadFilename(rawValue, fallback)
}

export const prepareObjectUrlDownload = (
  blob: Blob,
  contentDisposition: string | null | undefined,
  fallbackFilename: string,
): PreparedDownload => ({
  objectUrl: URL.createObjectURL(blob),
  filename: filenameFromContentDisposition(contentDisposition, fallbackFilename),
})

export const fetchDownloadBlob = async (
  url: string,
  fallbackFilename: string,
): Promise<PreparedDownload> => {
  const response = await fetch(url, { credentials: "include" })

  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`)
  }

  return prepareObjectUrlDownload(
    await response.blob(),
    response.headers.get("content-disposition"),
    fallbackFilename,
  )
}

export const triggerObjectUrlDownload = ({ objectUrl, filename }: PreparedDownload) => {
  const anchor = document.createElement("a")
  anchor.href = objectUrl
  anchor.download = filename
  anchor.style.display = "none"
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}
